import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { PageHeader } from "../components/shell/PageHeader";
import { Button } from "../components/ui/Button";
import { EmptyState, ErrorNotice, Notice, SkeletonRows } from "../components/ui/Feedback";
import { Field, SelectField } from "../components/ui/Input";
import { Cell, TableFrame, TableHead, TableRow } from "../components/ui/Table";
import type { Column } from "../components/ui/Table";
import { Metric, MicroLabel, SectionTitle } from "../components/ui/Text";
import { getPage, post } from "../lib/api";
import { count, dateOnly } from "../lib/format";
import { useSession } from "../lib/session";
import type { PeriodType, Policy } from "../lib/types";

/**
 * Allowance policies.
 *
 * ## There is no edit and no delete, and the page says so
 *
 * Spec 6.3 evaluates thresholds against a quota snapshot taken when the period
 * opened, so a mid-period edit would contradict a figure an operator has already
 * been told. Changing a card's quota means assigning it a different policy, which
 * leaves a dated history. A UI offering an Edit button that the API refuses would
 * be worse than one that explains the model — so this page explains it.
 */

/**
 * The fixed widths sum to 80%, leaving 20% for the un-sized thresholds column.
 * That column holds the longest content on the row — three percentages plus
 * separators — so starving it is what makes it wrap to three lines.
 */
const COLUMNS: readonly Column[] = [
  { key: "name", label: "Policy", width: "24%" },
  { key: "quota", label: "Quota", numeric: true, width: "12%" },
  { key: "period", label: "Period", width: "14%" },
  { key: "thresholds", label: "Warns at" },
  { key: "concurrent", label: "Concurrent", width: "12%" },
  { key: "assigned", label: "Holders", numeric: true, width: "8%" },
  { key: "created", label: "Created", numeric: true, width: "10%" },
];

const PERIOD_TYPES: readonly { readonly value: PeriodType; readonly label: string }[] = [
  { value: "calendar_month", label: "Calendar month" },
  { value: "calendar_week", label: "Calendar week (Mon–Sun)" },
  { value: "rolling_30d", label: "Rolling 30 days" },
];

export function Policies() {
  const { can } = useSession();
  const [isCreating, setIsCreating] = useState(false);

  const policies = useQuery({
    queryKey: ["allowance-policies"],
    queryFn: () => getPage<Policy>("/allowance-policies"),
  });

  const rows = policies.data?.rows ?? [];
  const assignedTotal = rows.reduce((total, row) => total + row.assignedHolderCount, 0);

  return (
    <>
      <PageHeader
        eyebrow="Allowances"
        title="Policies"
        metrics={
          <>
            <Metric label="Policies" value={count(rows.length)} />
            <Metric label="Assignments" value={count(assignedTotal)} hint="holders covered" />
            <Metric
              label="Unused"
              value={count(rows.filter((row) => row.assignedHolderCount === 0).length)}
              hint="no holder assigned"
              tone="muted"
            />
          </>
        }
        lede="A policy is created and then assigned. It is never edited — thresholds are evaluated against the quota as it stood when the period opened, so changing a live policy would move a number someone has already been told."
        actions={
          can("policies.create") && !isCreating ? (
            <Button variant="primary" onClick={() => setIsCreating(true)}>
              New policy
            </Button>
          ) : undefined
        }
      />

      {isCreating ? <CreateForm onDone={() => setIsCreating(false)} /> : null}

      {policies.isError ? (
        <ErrorNotice error={policies.error} onRetry={() => void policies.refetch()} />
      ) : null}

      <TableFrame>
        <TableHead columns={COLUMNS} />
        {policies.isPending ? (
          <SkeletonRows rows={4} columns={COLUMNS.length} />
        ) : (
          <tbody>
            {rows.map((row, index) => (
              <TableRow key={row.id} index={index}>
                <Cell>
                  <span className="font-medium text-ink">{row.name}</span>
                </Cell>
                <Cell numeric>
                  <span className="font-semibold">{count(row.quotaKwh)}</span>
                  <span className="text-ink-faint"> kWh</span>
                </Cell>
                <Cell muted>{row.periodType.replace(/_/g, " ")}</Cell>
                <Cell muted className="whitespace-nowrap">
                  {row.warnThresholds.map((threshold) => `${threshold}%`).join(" · ")}
                </Cell>
                <Cell muted={!row.allowConcurrentTx}>
                  {row.allowConcurrentTx ? "Allowed" : "One at a time"}
                </Cell>
                <Cell numeric muted={row.assignedHolderCount === 0}>
                  {row.assignedHolderCount}
                </Cell>
                <Cell numeric muted>
                  {dateOnly(row.createdAt, "UTC")}
                </Cell>
              </TableRow>
            ))}
          </tbody>
        )}
      </TableFrame>

      {!policies.isPending && rows.length === 0 ? (
        <EmptyState
          title="No allowance policies yet."
          detail="Until a policy exists and is assigned, every card is tracked without a quota — energy is recorded and nothing is ever flagged."
          action={
            can("policies.create") && !isCreating ? (
              <Button variant="primary" onClick={() => setIsCreating(true)}>
                Create the first policy
              </Button>
            ) : undefined
          }
        />
      ) : null}
    </>
  );
}

/**
 * Creating a policy.
 *
 * Quota is entered in whole kWh because that is the unit an operator thinks in and
 * because the server refuses a fractional value rather than rounding it — a "1.5
 * kWh" that silently became 1 or 2 is exactly the quiet wrongness the integer-only
 * accounting path exists to prevent. The input enforces the same rule client-side
 * so the refusal is immediate rather than a round trip.
 */
function CreateForm({ onDone }: { readonly onDone: () => void }) {
  const queryClient = useQueryClient();

  const [name, setName] = useState("");
  const [quotaKwh, setQuotaKwh] = useState("");
  const [periodType, setPeriodType] = useState<PeriodType>("calendar_month");
  const [thresholds, setThresholds] = useState("80, 100");
  const [allowConcurrentTx, setAllowConcurrentTx] = useState(false);
  const [notice, setNotice] = useState<string | undefined>(undefined);

  const create = useMutation({
    mutationFn: (body: unknown) => post<Policy>("/allowance-policies", body),
    onSuccess: (policy) => {
      setNotice(`${policy.name} created.`);
      void queryClient.invalidateQueries({ queryKey: ["allowance-policies"] });
      onDone();
    },
  });

  const parsedQuota = Number(quotaKwh);
  const quotaError =
    quotaKwh === ""
      ? undefined
      : !Number.isInteger(parsedQuota) || parsedQuota <= 0
        ? "Whole kWh only, and greater than zero. Fractional quotas are refused rather than rounded."
        : undefined;

  const parsedThresholds = parseThresholds(thresholds);
  const thresholdError =
    parsedThresholds === undefined
      ? "Whole percentages between 1 and 1000, comma separated."
      : undefined;

  const canSubmit =
    name.trim() !== "" &&
    quotaKwh !== "" &&
    quotaError === undefined &&
    thresholdError === undefined &&
    !create.isPending;

  return (
    <section className="flex flex-col gap-4 border-l-2 border-ink bg-paper-raised px-4 py-4">
      <SectionTitle
        action={
          <Button variant="ghost" onClick={onDone}>
            Cancel
          </Button>
        }
      >
        New policy
      </SectionTitle>

      {notice === undefined ? null : <Notice tone="ok">{notice}</Notice>}
      {create.isError ? <ErrorNotice error={create.error} /> : null}

      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (!canSubmit || parsedThresholds === undefined) return;
          create.mutate({
            name: name.trim(),
            quotaKwh: parsedQuota,
            periodType,
            warnThresholds: parsedThresholds,
            allowConcurrentTx,
          });
        }}
        className="grid grid-cols-1 gap-x-6 gap-y-5 md:grid-cols-2 xl:grid-cols-4"
      >
        <Field
          label="Name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Fleet standard"
          required
          autoFocus
        />

        <Field
          label="Quota (kWh)"
          type="number"
          inputMode="numeric"
          step={1}
          min={1}
          value={quotaKwh}
          onChange={(event) => setQuotaKwh(event.target.value)}
          placeholder="500"
          required
          {...(quotaError === undefined ? {} : { error: quotaError })}
        />

        <SelectField
          label="Period"
          value={periodType}
          onChange={(event) => setPeriodType(event.target.value as PeriodType)}
        >
          {PERIOD_TYPES.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </SelectField>

        <Field
          label="Warn at (%)"
          value={thresholds}
          onChange={(event) => setThresholds(event.target.value)}
          hint="Sorted and de-duplicated on save. Over 100 is allowed, for a hard overage alert."
          {...(thresholdError === undefined ? {} : { error: thresholdError })}
        />

        <div className="flex flex-col gap-1 md:col-span-2">
          <MicroLabel>Concurrent sessions</MicroLabel>
          <label className="flex items-start gap-2 text-body text-ink">
            <input
              type="checkbox"
              checked={allowConcurrentTx}
              onChange={(event) => setAllowConcurrentTx(event.target.checked)}
              className="mt-1 size-4 accent-signal"
            />
            <span>
              Allow one card to hold more than one live session.
              <span className="block text-micro tracking-normal normal-case text-ink-faint">
                Off by default — one card, one session, unless a depot genuinely needs otherwise.
              </span>
            </span>
          </label>
        </div>

        <div className="flex items-end md:col-span-2 xl:col-span-4">
          <Button type="submit" variant="primary" disabled={!canSubmit} pending={create.isPending}>
            Create policy
          </Button>
        </div>
      </form>

      <p className="m-0 max-w-prose text-micro tracking-normal normal-case text-ink-faint">
        Quotas never block a charge. Spec 6.2 keeps enforcement soft: an over-quota card still
        authorises and still starts a session, and the overage is reported rather than prevented.
      </p>
    </section>
  );
}

/**
 * Returns `undefined` for anything the server would reject, so the form can refuse
 * before the request. An empty list is invalid — the schema requires at least one
 * threshold — and so is a value outside 1–1000.
 */
function parseThresholds(raw: string): readonly number[] | undefined {
  const parts = raw
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part !== "");

  if (parts.length === 0 || parts.length > 16) return undefined;

  const values: number[] = [];
  for (const part of parts) {
    if (!/^\d+$/.test(part)) return undefined;
    const value = Number(part);
    if (value < 1 || value > 1000) return undefined;
    values.push(value);
  }

  return values;
}
